import { LicenseManager } from '@ag-grid-enterprise/core';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpService } from '@ats/api-sdk';
import { EfpAppConfigService } from '@ats/app-config';
import { ConfigService, FlagsService } from '@ats/service-base';
import { EfactoryPubSubService } from '@ats/service-pubsub';
import { EfactorySiteService } from '@ats/service-site';
import { EfactoryUserInfoService, EfactoryUserService, ILoggedInUser } from '@ats/service-user';
import {
  EfpServiceUserNotificationService,
  NotificationProgressDirection,
  NotificationType
} from '@ats/service-user-notification';
import { AuthService, User } from '@auth0/auth0-angular';
import { TranslocoService } from '@ngneat/transloco';
import { productFruits } from 'product-fruits';
import { filter, map, Observable, of, Subject, startWith, switchMap, takeUntil, tap } from 'rxjs';

interface NewRelicApi {
  [key: string]: unknown;
}

const EMPTY_LOGGED_IN_USER: ILoggedInUser = {
  personId: '',
  username: '',
  portalusername: '',
  cosmosId: '',
  tenantId: 1,
  firstName: '',
  lastName: '',
  fullName: '',
  email: '',
  sites: [],
  roles: ['User'],
  locale: '',
  defaultSiteId: '',
  currentSiteId: '',
  currentSite: null,
  preferences: []
};

// NewRelic object from global namespace.
declare var newrelic: NewRelicApi;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private ct: EfactoryPubSubService,
    private router: Router,
    private siteService: EfactorySiteService,
    private translate: TranslocoService,
    private userService: EfactoryUserService,
    private userInfoService: EfactoryUserInfoService,
    private userNotification: EfpServiceUserNotificationService,
    private appconfig: EfpAppConfigService,
    private httpService: HttpService,
    private flagsService: FlagsService
  ) {
    this.httpService.config = {
      apiHost: appconfig.config.uris.aks
    };
  }

  /**
   * build number
   */
  buildNumber!: string;
  /**
   * The logged in user person record.
   */
  userPerson!: ILoggedInUser;

  mastheadFlagsmithEnvId!: string;
  /**
   * Whether user is authenticated.
   */
  isAuthenticated: Observable<boolean> = this.authService.isAuthenticated$;
  /**
   * OrgId used for redirects if authentication error occurs.
   */
  userOrgId!: string;
  /**
   * Site id for current site that user is using
   */
  userSiteId!: string;
  /**
   * user id or email that was used for authentication
   */
  userId!: string;
  /**
   * Whether user information is loaded
   */
  userReady = false;
  /**
   * Whether user is a supervisor
   */
  isSupervisor = false;
  /**
   * Whether a full screen form is shown.
   */
  isFormShown = false;
  /**
   * Container being viewed
   */
  currentRoute!: string;
  /**
   * Flagsmith flags used by this component
   */
  featureToggles$ = this.flagsService.featureFlagsForPage('light_dark_mode_switch');

  /**
   * Login pipeline
   */
  private readonly authenticatedSite$ = this.authService.user$.pipe(
    filter((user): user is User => !!user && Object.keys(user).length > 0),
    // Set global variables
    tap((user) => {
      this.userOrgId = user['org_id'] as string;
      this.userId = user.email as string;
      this.userSiteId = user['https://efactorypro.com/siteid'] as string;
      this.userPerson = {
        personId: user['https://efactorypro.com/id'],
        cosmosId: user['https://efactorypro.com/uuid'],
        tenantId: 1,
        firstName: '',
        lastName: '',
        fullName: '',
        email: user.email,
        sites: [],
        roles: user['https://efactorypro.com/roles'],
        locale: '',
        defaultSiteId: user['https://efactorypro.com/siteid'],
        currentSiteId: user['https://efactorypro.com/siteid'],
        currentSite: null,
        preferences: []
      } as unknown as ILoggedInUser;
    }),
    // Initialize NewRelic
    tap((user) => {
      // Set custom NewRelic attributes for session traces
      // biome-ignore lint/suspicious/noExplicitAny: Allowing for window object
      (window as any)['newrelic']?.setCustomAttribute('user', user.email);
    }),
    // Initialize Flagsmith
    tap((user) => {
      this.initBulletTrainFeatureToggles(user['https://efactorypro.com/id']);
    }),
    // Apply theme
    tap((user) => {
      const userId = user.email?.toLowerCase();
      // Apply dark/light mode for user
      const theme = localStorage.getItem(`efp-theme-${userId}`) === 'light' ? 'light' : 'dark';
      localStorage.setItem(`efp-theme-${userId}`, theme);
      document.documentElement.classList.add(`${theme}Mode`);

      // High contrast mode
      const mode =
        localStorage.getItem(`efp-high-contrast-${userId}`) === 'normal' ? 'normal' : 'high';
      localStorage.setItem(`efp-high-contrast-${userId}`, mode);
      document.documentElement.classList.add(`${mode}Contrast`);

      this.initProductFruits(user);
    }),
    // Get the site information
    switchMap((user) => this.siteService.getSite(user['https://efactorypro.com/siteid'])),
    // Get site sublocations, teams, and part locations
    switchMap((site) =>
      // biome-ignore lint/suspicious/noExplicitAny: Allowing as code handles the null
      this.siteService.loadSiteLists(null as any, this.userSiteId).pipe(map(() => site))
    )
  );

  user$: Observable<ILoggedInUser> = this.authenticatedSite$.pipe(
    switchMap((site) =>
      this.userService.getUserInfo(this.userPerson.cosmosId).pipe(map((user) => ({ user, site })))
    ),
    // Set the user in userInfo service
    map(({ user, site }): ILoggedInUser => {
      this.userPerson = this.userInfoService.set(user);
      this.userInfoService.setCurrentSite(site);

      // Announce Current Site is ready
      this.ct.$pub('site:ready');

      // User Preferences setup and initial loading.
      this.userInfoService.getModulePreferences('controlcenter', this.userPreferenceBaseObject());

      // Announce user is ready
      this.ct.$pub('user:ready', { person: this.userInfoService.getUser() });

      return this.userPerson;
    }),
    // Set default part location for user
    tap(() => {
      this.userInfoService.setDefaultLocation(this.siteService.getDefaultLocationForCurrentSite());
      this.ct.$pub('site:siteIdChanged', this.siteService.currentCosmosSiteId);
    }),
    startWith(EMPTY_LOGGED_IN_USER)
  );
  /**
   * Subject used for unsubscribing PubSub subscriptions
   */
  private stopSubscriptions$ = new Subject<void>();

  ngOnInit(): void {
    // AG-Grid License Key
    // eslint-disable-next-line max-len
    LicenseManager.setLicenseKey(
      'Using_this_{AG_Grid}_Enterprise_key_{AG-096369}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ATS}_is_granted_a_{Multiple_Applications}_Developer_License_for_{3}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{4}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{9_February_2027}____[v3]_[01]_MTgwMjEzMTIwMDAwMA==4b4591a810f0f1e79a145c933e1a9180'
    );

    this.mastheadFlagsmithEnvId = this.appconfig.config.mastheadFlagsmithEnvId as string;

    this.configService.setUris(this.appconfig.config.uris);

    this.startSubscriptions();

    this.initTranslationService();

    // this.startAuth();

    // Expose build number for display. Regex strips -alpha nad -beta tags and supports local environment.
    const buildNumberRaw = this.appconfig.config.buildNumber as string;
    const match = buildNumberRaw.match(/^\d*\.\d*\.\d*|local/);
    this.buildNumber = match ? match[0] : '';
  }

  ngOnDestroy(): void {
    this.stopSubscriptions$.next();
    this.stopSubscriptions$.complete();
  }

  startAuth(): void {
    // Subscribe to auth state
    this.isAuthenticated = this.authService.isAuthenticated$;
  }

  initProductFruits(user: User): void {
    productFruits.init(
      'Fv0sEjXxNSXcmo1A',
      navigator.languages[0].split('-')?.[0].toLowerCase() ?? 'en',
      { username: user.email as string }
    );
  }

  startSubscriptions(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e: NavigationEnd) => {
          const firstPathSegment = e.url.split('/')[1]?.split('?')[0];

          if (firstPathSegment) {
            this.currentRoute = firstPathSegment;
          }
        }),
        takeUntil(this.stopSubscriptions$)
      )
      .subscribe();

    // this.ct
    //   .$sub('siteSelection:formShown')
    //   .pipe(takeUntil(this.stopSubscriptions$))
    //   .subscribe((payload: boolean) => {
    //     this.isFormShown = payload;
    //   });
  }

  routeTo(routePath: string): void {
    this.protectForm().subscribe((value) => {
      if (value) {
        this.router.navigate([routePath]);
      }
    });
  }

  initBulletTrainFeatureToggles(personId: string): void {
    this.flagsService.initializeFlagsmith(
      this.appconfig.config.bulletTrainEnvId as string,
      personId
    );
  }

  initTranslationService(): void {
    let lang: string = navigator.language;

    if (lang.toLowerCase().substring(0, 2) === 'en') {
      lang = 'en-US';
    } else if (lang.toLowerCase().substring(0, 2) === 'es') {
      lang = 'es-MX';
    }

    this.translate.setDefaultLang(lang);
  }

  protectForm(): Observable<boolean> {
    if (this.isFormShown) {
      return this.userNotification
        .showObservable({
          type: NotificationType.Warning,
          // eslint-disable-next-line max-len
          text: this.translate.translate(
            `Attention: If you have any unsaved changes, they will be lost. Are you sure that you'd like to continue?`
          ) as string,
          action1: true,
          action1Text: this.translate.translate('Continue') as string,
          action1OnDefault: false,
          action2: true,
          action2Text: this.translate.translate('Stay Here') as string,
          action2OnDefault: true,
          progressBar: true,
          progressAnimation: NotificationProgressDirection.Increasing
        })
        .pipe(map((value: { action: boolean }) => value.action));
    }

    return of(true);
  }

  userPreferenceBaseObject() {
    return {};
  }
}
